import React, {useRef, useState} from "react";
import {Box} from "@mui/system";
import _ from "lodash";
import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router-dom";
import {
    formattedResponseText,
    formatText,
    getCurrentAgentName,
    getCurrentEmployment,
    getEditorConfig,
    getObjectProperty,
    isHtmlHandle,
    isObjectEmpty,
    parseHtmlWithImage,
    setObjectProperty
} from "_helpers";
import ResponseEditor from "./ResponseEditor";
import {dispatcherConstants, handleType, msgSource, responseStatus, workMode, supportedSourcesForCloseTicket} from "_constants";
import {dispatcherActions, macroActions, voteActions} from "_actions";
import ResponseActions from "./ResponseActions";
import useViewHtmlBody from "../../../Utilities/hooks/useViewHtmlBody";
import {
    buildAdminResponsePayload,
    buildSubmitAndHidePayload,
    buildSubmitPerfectResponse,
    buildSubmitResponsePayload,
} from "../../../Utilities/VoteUtils";
import useViewSubmitVote from "../../../Utilities/hooks/useViewSubmitVote";
import MessageSubject from "../../../../../common/Message/MessageSubject";
import {CrossDispatchWarningModal} from "../../../../Messages/CrossDispatchWarningModal";
import useViewMessageDetails from "../../../../Messages/MessageDetails/Utilities/hooks/useViewMessageDetails";
import {getPlatform, PLATFORM} from "../../../../Messages/MessageDetails/Utilities/MessageDetailsUtils";
import {filterCustomFields, isCustomFieldSupported, validateCustomFieldsInput} from "./CustomField/CustomFieldUtils";
import {SnackBar} from "../../../../../../common";
import useFetchMacro from "./CustomField/hooks/useFetchMacroCustomFields";

export default function ResponseItem({
                                         response,
                                         index,
                                         count,
                                         isScrollComplete,
                                         isResponseSelected,
                                         isEditing,
                                         handleOpenEditResponse,
                                         selectedResponseId,
                                         editedResponseObj,
                                         handleCloseEditResponse,
                                         editedSubjectObj,
                                         customFields,
                                         loadingCustomFields,
                                         shouldRenderCustomFields,
                                     }) {
    const dispatch = useDispatch();
    const editorRef = useRef();
    const vote = useSelector(state => state.vote);
    const [attachments, setAttachments] = useState([]);
    const [crossDispatchWarningModal, setCrossDispatchWarningModal] = useState(false);
    const [crossDispatchWarningDismissed, setCrossDispatchWarningDismissed] = useState(false);
    const [selectedTags, setSelectedTags] = useState([]);
    const params = useParams();
    const {htmlBody, loadingHtmlBody} = useViewHtmlBody();
    const {submitVoteInit, submitVoteLoading} = useViewSubmitVote();
    const adminMessageId = _.get(params, 'id');
    const { message } = useViewMessageDetails();
    const platform = getPlatform(_.get(message, 'id'));
    const messageHandleType = _.get(message, 'dataSourceHandle.type') || _.get(message, 'handle.type.name');
    const sourceName = _.get(message, 'sourceName') || _.get(message, 'msgDataSource.msgSource.name');
    const canBeHidden = _.get(message, 'canBeHidden') || _.get(message, 'teamsAttributes.canBeHidden');
    const msgDataSource = _.get(message, 'msgDataSource');
    const author = _.get(message, 'author');
    const [customFieldsInput, setCustomFieldsInput] = useState([]);
    const [macroToFetch, setMacroToFetch] = useState(null);
    const { dataSourceHandle, } = message;
    const { macrosCustomField, fetchMacroLoading } = useFetchMacro(macroToFetch?.macroId, macroToFetch?.msgDataSource, sourceName);
    const filterCustomFieldList = filterCustomFields(customFields, macrosCustomField);
    const {id, responseMessageId, text, addedById, subject} = response;
    const agentName = getCurrentAgentName();
    const responseText = formattedResponseText(text, author, agentName, messageHandleType);
    const isMacro = _.get(msgDataSource, 'isPlatformMacroEnabled');
    const {isCharDisplay, charLength} = getEditorConfig(sourceName);
    const hasHtml = isHtmlHandle(messageHandleType, sourceName);
    const isEmail = messageHandleType === _.get(handleType, 'email');
    let isSelected = isResponseSelected && selectedResponseId === id;
    const isEditSelected = isEditing && selectedResponseId === id;
    const isEditingText = !isObjectEmpty(getObjectProperty(editedResponseObj, id));
    const responseLabel = isMacro ? response.title : `Response ${index + 1} of ${count}`;
    const includedResponseId = Object.keys(editedResponseObj || {}).includes(id);
    const currentEmployment = getCurrentEmployment();
    const isLiveAgent = _.get(currentEmployment, 'mode') === workMode.ACTIVE;
    let status;
    let openEditor = false;
    let editedResponse = '';
    const hasSubject = !_.isEmpty(subject);
    const hasResponse = !_.isEmpty(response);
    const source = _.toString(message?.sourceName).toLowerCase();
    const isCloseTicketSupported = supportedSourcesForCloseTicket.includes(source);
    const [closeTicket, setCloseTicket] = useState(false);
    const shouldAddCustomFields = shouldRenderCustomFields;
    const dispatchMessageLoading = useSelector(state => state.dispatcher.request)
    // the submit loading flags are shared by all response items, so only the clicked button shows a spinner
    const [pendingAction, setPendingAction] = useState(null);
    const isSubmitting = submitVoteLoading || dispatchMessageLoading;
    const submitLoading = isSubmitting && pendingAction === PENDING_ACTION.SUBMIT;
    const perfectLoading = isSubmitting && pendingAction === PENDING_ACTION.PERFECT;

    if((isEmail && isEditingText) || !isEmail) {
        openEditor = isEditSelected;
    }
    else if(isEmail) {
        openEditor = isEditSelected && !loadingHtmlBody && !_.isEmpty(htmlBody);
    }
    if(!hasResponse) {
        openEditor = true;
    }

    if(!_.isEmpty(addedById) && !includedResponseId) {
        status = responseStatus.JUST_ADDED;
    }
    else if(includedResponseId) {
        status = responseStatus.EDITED;
    }

    const showCrossDispatchWarningModal = () => {
        setCrossDispatchWarningModal(true);
    }

    const resetCrossDispatchWarningModal = () => {
        setCrossDispatchWarningModal(true);
    }

    const closeCrossDispatchWarningModal = () => {
        setCrossDispatchWarningModal(false);
        setCrossDispatchWarningDismissed(true);
    }

    const clearCrossDispatchWarningDismissed = () => {
        setCrossDispatchWarningDismissed(false);
    }

    const isPossibleCrossDispatchDetected = () => {
        return !vote?.loading && vote?.error?.error?.errorCode === dispatcherConstants.POSSIBLE_CROSS_DISPATCH_ERROR_CODE;
    }

    /**
     * Get the html body for a particular message using the response Id.
     * This triggers an event to the server to make a request to the external source of a message
     * to retrieve the html body
     * @param responseId
     */
    const getHtmlBody = (responseId) => {
        dispatch(dispatcherActions.getHtmlBody(responseId));
    };

    const handleCloseEditor = () => {
        handleCloseEditResponse();
    }

    const handleEditedResponse = (editorText) => {
        editedResponse = editorText;
    }

    const clearEditedResponse = () => {
        handleEditedResponse('');
    }

    const clearAttachment = () => {
        _.remove(attachments, () => true);
        setAttachments(attachments);
    };

    const clearTags = () => {
        _.remove(selectedTags, () => true);
        setSelectedTags(selectedTags);
    }

    const handleClearEditor = () => {
        editorRef?.current?.clearEditor();
        clearEditedResponse();
        clearAttachment();
        clearTags();
        clearCrossDispatchWarningDismissed();
    }

    const handleSubmitResponse = () => {
        const isValidCustomFields = validateCustomFieldsInput(filterCustomFieldList, customFieldsInput, shouldAddCustomFields);
        if(!isValidCustomFields?.isValid) {
            SnackBar.showMessage(isValidCustomFields?.message);
            return;
        }

        const subjectObj = getSubject();
        editedResponse = editedResponse || _.get(getObjectProperty(editedResponseObj, id), 'text');
        if(platform === PLATFORM.VOTE) {
            handleAgentResponse(responseText, subjectObj, editedResponse);
        } else {
            handleAdminResponse(responseText, subjectObj, editedResponse);
        }
    }

    const handleAgentResponse = (responseText, subjectObj, editedResponse) => {
        const payload = buildSubmitResponsePayload(message, response, editedResponse, attachments, subjectObj, closeTicket, crossDispatchWarningDismissed, selectedTags, customFieldsInput);
        setPendingAction(PENDING_ACTION.SUBMIT);
        dispatch(voteActions.submitResponse(payload))
            .then(() => resetCrossDispatchWarningModal())
            .finally(() => setPendingAction(null));
    }

    const handleAgentPerfectResponse = () => {
        const isValidCustomFields = validateCustomFieldsInput(filterCustomFieldList, customFieldsInput, shouldAddCustomFields);
        if(!isValidCustomFields?.isValid) {
            SnackBar.showMessage(isValidCustomFields?.message);
            return;
        }

        const subjectObj = getSubject();
        const payload = buildSubmitPerfectResponse(message, response, subjectObj, closeTicket, crossDispatchWarningDismissed, customFieldsInput);
        setPendingAction(PENDING_ACTION.PERFECT);
        dispatch(voteActions.submitResponse(payload))
            .then(() => resetCrossDispatchWarningModal())
            .finally(() => setPendingAction(null));
    }

    const handleAdminResponse = (responseText, subjectObj, editedResponse) => {
        const payload = buildAdminResponsePayload(message, adminMessageId, responseText, editedResponse, attachments, subjectObj, closeTicket, crossDispatchWarningDismissed, selectedTags, customFieldsInput);
        setPendingAction(PENDING_ACTION.SUBMIT);
        dispatch(dispatcherActions.dispatchMessage(message.id, payload))
            .then((result) => {
                if (result.success) {
                    handleClearEditor();
                } else if (!result.success && result?.response?.errorCode === dispatcherConstants.POSSIBLE_CROSS_DISPATCH_ERROR_CODE) {
                    showCrossDispatchWarningModal();
                }
            }).catch(error => console.error('An error occurred when dispatching message: ' + error))
            .finally(() => setPendingAction(null));
    }

    const handleSubmitPerfectResponse = () => {
        const subjectObj = getSubject();
        if(platform === PLATFORM.VOTE) {
            handleAgentPerfectResponse(subjectObj);
        } else {
            handleAdminResponse(_.get(response, 'text'), subjectObj, editedResponse);
        }
    }

    const handleSubmitAndHide = () => {
        const payload = buildSubmitAndHidePayload(message);
        dispatch(voteActions.submitAndHide(message.id, payload));
    }

    const handleSubjectChange = (responseId, subject) => {
        setObjectProperty(editedSubjectObj, responseId, { subject: subject });
    }

    const getSubject = () => {
        return {subject, editedSubject: _.get(getObjectProperty(editedSubjectObj, id), 'subject')};
    }

    const handleOpenEditor = (responseId, isEditingText, msgDataSource, responseOption) => {
        dispatch(dispatcherActions.clearHtmlBody());
        if (isMacro) {
            if (isObjectEmpty(getObjectProperty(editedResponseObj, _.get(responseOption, 'id')))) {
                let text = _.get(responseOption, 'htmlBody') || _.get(responseOption, 'text');
                text = formatText(text, sourceName);
                setObjectProperty(editedResponseObj, _.get(responseOption, 'id'), { text: text });
            }
            if(isCustomFieldSupported(sourceName) && responseOption?.macroId) {
                setMacroToFetch({macroId: responseOption?.macroId, msgDataSource: msgDataSource?.id});
            }
        }
        else if(!isEditingText && messageHandleType === _.get(handleType, 'email')) getHtmlBody(responseId);
        handleOpenEditResponse(responseId);
    }

    const handleCloseTicket = ({ value }) => {
        setCloseTicket(value);
    };

    const handleTagSelection = (tags) => {
        setSelectedTags(tags);
    };

    const handleRemoveTag = (tagName) => {
        setSelectedTags(() =>
            selectedTags.filter((selectedTag) => _.get(selectedTag, 'name') !== tagName)
        );
    };

    const handleCustomFields = (customFields) => {
        setCustomFieldsInput(customFields);
    };

    const Content = () => {
        if(openEditor) {
            return(
                <ResponseEditor
                    responseId={id}
                    text={responseText}
                    messageHandleType={messageHandleType}
                    sourceName={sourceName}
                    editedResponseObj={editedResponseObj}
                    isEmail={hasHtml}
                    charLength={charLength}
                    isCharDisplay={isCharDisplay}
                    attachments={attachments}
                    setAttachments={setAttachments}
                    handleEditedResponse={handleEditedResponse}
                    selectedTags={selectedTags}
                    handleRemoveTag={handleRemoveTag}
                    editorRef={editorRef}
                />
            );
        }
        return <Box>{ parseHtmlWithImage(text) }</Box>;
        {/*@TODO://Add `show more` component*/}
    }

    return(
        <Box key={index} component={'li'} sx={{...style.responseItem, ... openEditor && style.responseSelected}}>
            <Box sx={{marginBottom: '7px'}}>
                { hasResponse &&
                    <Box sx={style.responseDescContainer}>
                        <Box sx={style.responseLabel}>{responseLabel}</Box>
                        {status && <Box sx={style.responseStatus}>{status}</Box>}
                    </Box>
                }
                { hasSubject &&
                    <MessageSubject
                        subjectParentId={id}
                        subject={subject}
                        handleSubjectChange={handleSubjectChange}
                        editedSubjectObj={editedSubjectObj}
                        isEnabled={isScrollComplete}
                    />
                }
            </Box>

            <Box sx={style.responseText}>
                <Content />
            </Box>
            <Box sx={{marginTop: '10px'}}>
                <ResponseActions
                    responseId={id}
                    isEditSelected={isEditSelected}
                    loadingHtmlBody={loadingHtmlBody}
                    isScrollComplete={isScrollComplete}
                    handleCloseEditor={handleCloseEditor}
                    handleOpenEditor={handleOpenEditor}
                    isEditingText={isEditingText}
                    msgDataSource={msgDataSource}
                    canBeHidden={canBeHidden}
                    response={response}
                    openEditor={openEditor}
                    handleSubmitResponse={handleSubmitResponse}
                    handleSubmitAndHide={handleSubmitAndHide}
                    submitVoteLoading={submitVoteLoading}
                    submitLoading={submitLoading}
                    perfectLoading={perfectLoading}
                    isLiveAgent={isLiveAgent}
                    handleSubmitPerfectResponse={handleSubmitPerfectResponse}
                    sourceName={sourceName}
                    handleCloseTicket={handleCloseTicket}
                    handleTagSelection={handleTagSelection}
                    selectedTags={selectedTags}
                    customFields={customFields}
                    loadingCustomFields={loadingCustomFields}
                    customFieldsInput={customFieldsInput}
                    shouldAddCustomFields={shouldAddCustomFields}
                    handleCustomFields={handleCustomFields}
                    filterCustomFieldList={filterCustomFieldList}
                    closeTicket={closeTicket}
                    platform={platform}
                    dispatchMessageLoading={dispatchMessageLoading}
                />
            </Box>
            <CrossDispatchWarningModal
                open={crossDispatchWarningModal && isPossibleCrossDispatchDetected()}
                handleClose={closeCrossDispatchWarningModal}
            />
        </Box>
    )
}

const PENDING_ACTION = {
    SUBMIT: 'submit',
    PERFECT: 'perfect',
};

const style = {
    responseItem: {
        padding: '8px 10px 10px 10px',
        backgroundColor: '#F3F5F6',
        borderStyle: 'solid',
        borderWidth: 1,
        borderTopWidth: 0,
        borderColor: '#DCE2E3',
        '&:first-of-type': {borderTopLeftRadius: '6px', borderTopRightRadius: '6px', borderTopWidth: '1px'},
        '&:last-of-type': {borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px'},
    },
    responseSelected: {
        backgroundColor: '#FFFFFF'
    },
    responseLabel: {
        fontFamily: 'LarsseitMedium',
        fontSize: '14px',
        color: '#495D5E',
        py: '0px'
    },
    responseStatus: {
        fontFamily: 'LarsseitMedium',
        fontSize: '14px',
        backgroundColor: '#DBD8FF',
        color: '#0C2728',
        borderRadius: '3px',
        p: '3px 10px'
    },
    responseText: {
        fontFamily: 'LarsseitRegular',
        fontSize: '16px',
        color: '#0C2728',
        overflowWrap: 'break-word',
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
    responseDescContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1px',
    }
}
